import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Form, Input, message, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import moment, { Moment } from 'moment/moment'
import InputMask from 'react-input-mask'
import { patchRequestAuth, postRequestAuth } from '../../../request/request'
import validatorServerErrors from '../../../modules/ValidatorServerErrors/ValidatorServerErrors'
import '../style/myTableStyle.scss'
import { validateAge } from '../../../function/validateAge'

interface Props {
  isModalOpen: boolean
  clearForm: boolean
  setReload: any
  editData: any
  typeModal?: string | undefined | null
  setIsModalOpen: (e: boolean) => void
}

const UserManagementModal: React.FC<Props> = ({
  isModalOpen,
  setIsModalOpen,
  setReload,
  editData,
  clearForm,
  typeModal,
}) => {
  const { t } = useTranslation()
  const [add] = Form.useForm()
  const [loading, setLoading] = useState<boolean>(false)
  const [validateErrors, setValidateErrors]: any = useState({})
  const [userData, setUserData] = useState<any>(null)
  const dateFormat = 'YYYY-MM-DD'
  const phoneMask = '+375(99)999-99-99'
  const regexName = new RegExp('^[a-zA-Zа-яА-Я]+$|^$')
  const regexEmail = new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$|^$')
  let timeOut: any

  useEffect(() => {
    setValidateErrors([])
    add.resetFields()
  }, [clearForm])

  useEffect(() => {
    const { id, name, date_birthday, phone, email, last_name, first_name, middle_name } = editData || {}
    const initDate = typeModal === 'add' ? moment() : null;
    const newDate: Moment | null = date_birthday ? moment(date_birthday, dateFormat) : initDate;
    const dataObj = {
      id,
      name,
      date_birthday: newDate,
      phone,
      email,
      last_name,
      first_name,
      middle_name,
    }
    add.setFieldsValue(dataObj)
    setUserData(dataObj)
    setValidateErrors([])
  }, [editData, isModalOpen, typeModal])

  const disabledDate = (current: any) => {
    const minDate = new Date(1900, 0, 1)
    const maxDate = moment()
    return current && (current < minDate || current > maxDate)
  }

  const handleFinish = async (values: any) => {
    const { email, phone, first_name, last_name, middle_name, date_birthday } = values || {}
    if (typeModal === 'add') {
      const reqData = {
        email,
        phone: phone?.replace(/[^\d+]/g, ''),
        profile: [
          {
            first_name,
            last_name,
            middle_name,
            date_birthday: date_birthday?.format(dateFormat),
          },
        ],
      }
      postRequestAuth('/auth/create_user/', reqData)
        .then(() => {
          handleCancel()
          setReload((prev: boolean) => !prev)
          message.success(t('h80'))
        })
        .catch((err) => {
          setLoading(false)
          validatorServerErrors(err.data, setValidateErrors)
        })
    }

    if (typeModal === 'edit') {
      const reqData = {
        email,
        phone: phone?.replace(/[^\d+]/g, ''),
        profile: [
          {
            id: userData?.id,
            first_name,
            last_name,
            middle_name,
            date_birthday: date_birthday?.format(dateFormat) ?? null,
          },
        ],
      }
      patchRequestAuth(`/auth/update_user/${userData.id}/`, reqData)
        .then(() => {
          handleCancel()
          setReload((prev: boolean) => !prev)
          message.success(t('h80'))
        })
        .catch((err) => {
          setLoading(false)
          validatorServerErrors(err.data, setValidateErrors)
        })
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    timeOut = setTimeout(() => {
      add.resetFields()
      setLoading(false)
    }, 200)
  }

  const deleteUser = () => {
    patchRequestAuth(`/auth/deactivate_user/${userData.id}/`)
      .then(() => {
        handleCancel()
        setReload((prev: boolean) => !prev)
        message.success(t('h80'))
      })
      .catch((err) => {
        setLoading(false)
        validatorServerErrors(err.data, setValidateErrors)
      })
  }

  const modalTitle = () => {
    switch (typeModal) {
      case 'add':
        return t('profile.p8')
      case 'edit':
        return `${t('h180')}: ${editData?.name || '-'}`
      case 'delete':
        return `${t('h191')}: ${editData?.name || '-'} ?`
      default:
        return `${t('h180')}: ${editData?.name || '-'}`
    }
  }

  const validateLastName = (rule: any, value: string, callback: any) => {
    regexName.test(value) ? callback() : callback(t('h198'))
  }

  const validateFirstName = (rule: any, value: string, callback: any) => {
    regexName.test(value) ? callback() : callback(t('h195'))
  }

  const validateMiddleName = (rule: any, value: string, callback: any) => {
    regexName.test(value) ? callback() : callback(t('h199'))
  }

  const validateEmail = (rule: any, value: string, callback: any) => {
    regexEmail.test(value) ? callback() : callback(t('h196'))
  }

  return (
    <Modal title={modalTitle()} width={500} open={isModalOpen} onCancel={handleCancel} className="ptb-0" footer={false}>
      {typeModal !== 'delete' ? (
        <Form name="add" layout="vertical" form={add} autoComplete="off" onFinish={handleFinish}>
          <Form.Item
            name="last_name"
            label={t('h61')}
            rules={[{ required: true, message: t('h182') }, { validator: validateLastName }]}
            validateStatus={validateErrors['profile.0.last_name'] ? 'error' : ''}
            help={validateErrors['profile.0.last_name']}
          >
            <Input style={{ width: '100%' }} placeholder={t('h182')} />
          </Form.Item>

          <Form.Item
            name="first_name"
            label={t('h62')}
            rules={[{ required: false, message: t('h183') }, { validator: validateFirstName }]}
            validateStatus={validateErrors['profile.0.first_name'] ? 'error' : ''}
            help={validateErrors['profile.0.first_name']}
          >
            <Input style={{ width: '100%' }} placeholder={t('h183')} />
          </Form.Item>

          <Form.Item
            name="middle_name"
            label={t('h63')}
            rules={[{ required: false, message: t('h184') }, { validator: validateMiddleName }]}
            validateStatus={validateErrors['profile.0.middle_name'] ? 'error' : ''}
            help={validateErrors['profile.0.middle_name']}
          >
            <Input style={{ width: '100%' }} placeholder={t('h184')} />
          </Form.Item>

          <Form.Item
            name="date_birthday"
            label={t('h65')}
            rules={[
              {
                validator: (_, value) => validateAge(value, 18, t),
              },
            ]}
            validateStatus={validateErrors?.date_birthday ? 'error' : ''}
            help={validateErrors?.date_birthday}
          >
            <DatePicker
              showToday={false}
              placeholder={t('h185')}
              format={dateFormat}
              style={{ width: '100%' }}
              disabledDate={disabledDate}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label={t('h64')}
            rules={[{ required: true, message: t('h186') }]}
            validateStatus={validateErrors?.phone ? 'error' : ''}
            help={validateErrors?.phone}
          >
            <InputMask mask={phoneMask}>
              <Input style={{ width: '100%' }} placeholder={t('h186')} />
            </InputMask>
          </Form.Item>

          <Form.Item
            name="email"
            label={t('h49')}
            rules={[{ required: true, message: t('h187') }, { validator: validateEmail }]}
            validateStatus={validateErrors?.email ? 'error' : ''}
            help={validateErrors?.email}
          >
            <Input style={{ width: '100%' }} placeholder={t('h187')} />
          </Form.Item>

          <div className="text-center">
            <Button key="submit" htmlType="submit" type="primary" loading={loading} className="modalBtn">
              {typeModal === 'add' ? t('h188') : t('h60')}
            </Button>
          </div>
        </Form>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <Button type="primary" className="modalBtn" onClick={deleteUser}>
            {t('h213')}
          </Button>
          <Button
            type="primary"
            className="modalBtn"
            onClick={handleCancel}
            style={{ backgroundColor: 'gray', borderColor: 'gray' }}
          >
            {t('h214')}
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default UserManagementModal
